import { useState, useEffect, useContext } from 'react';
import api from '../services/api';
import { 
  Search, 
  ShoppingBag, 
  User, 
  Calendar, 
  ChevronLeft, 
  ChevronRight, 
  Download, 
  ArrowUpRight, 
  Eye, 
  Clock, 
  CreditCard, 
  FileText,
  Trash2,
  CheckSquare,
  Square
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';

export default function SalesHistory() {
  const { currencySymbol } = useSettings();
  const [sales, setSales] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [selectedIds, setSelectedIds] = useState(new Set());
  
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';

  const loadData = async () => {
    setLoading(true);
    try {
      const [salesRes, usersRes] = await Promise.all([
        api.get('/sales'),
        api.get('/auth/users')
      ]);
      setSales(salesRes.data);
      setUsers(usersRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const filteredSales = sales.filter(sale => {
    const matchesSearch = sale.id.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          (sale.customer?.name || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesUser = !selectedUser || sale.userId === parseInt(selectedUser);
    
    let matchesDate = true;
    if (dateRange.start) {
      matchesDate = new Date(sale.createdAt) >= new Date(dateRange.start);
    }
    if (dateRange.end && matchesDate) {
      const end = new Date(dateRange.end);
      end.setHours(23, 59, 59);
      matchesDate = new Date(sale.createdAt) <= end;
    }

    return matchesSearch && matchesUser && matchesDate;
  });

  // Pagination Logic
  const totalItems = filteredSales.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentSales = filteredSales.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const toggleSelect = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) newSelected.delete(id);
    else newSelected.add(id);
    setSelectedIds(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === currentSales.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(currentSales.map(s => s.id)));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this sale? This action cannot be undone.')) return;
    try {
      await api.delete(`/sales/${id}`);
      setSales(prev => prev.filter(s => s.id !== id));
      setSelectedIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    } catch (err) {
      alert(err.response?.data?.error || 'Error deleting sale');
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete ${selectedIds.size} sales?`)) return;
    const idsToDelete = Array.from(selectedIds);
    try {
      await api.post('/sales/bulk-delete', { ids: idsToDelete });
      setSales(prev => prev.filter(s => !selectedIds.has(s.id)));
      setSelectedIds(new Set());
    } catch (err) {
      alert(err.response?.data?.error || 'Error deleting sales');
    }
  };

  const getStats = () => {
    const today = new Date().toLocaleDateString();
    const todaySales = sales.filter(s => new Date(s.createdAt).toLocaleDateString() === today);
    return {
      total: sales.reduce((sum, s) => sum + s.total, 0),
      count: sales.length,
      todayCount: todaySales.length,
      todayTotal: todaySales.reduce((sum, s) => sum + s.total, 0)
    };
  };

  const stats = getStats();

  return (
    <div className="space-y-8 animate-fade-in-up pb-10">
      <div className="flex justify-between items-end">
        <div>
          <h2 className="text-3xl font-black text-theme-main uppercase tracking-tight flex items-center gap-3">
            <ShoppingBag size={32} className="text-brand" /> Sales Ledger
          </h2>
          <p className="text-theme-muted mt-1 font-bold uppercase tracking-widest text-xs">Verify, audit, and track all store transactions.</p>
        </div>
        <div className="flex gap-3">
          {isAdmin && selectedIds.size > 0 && (
            <button 
              onClick={handleBulkDelete}
              className="bg-rose-500 hover:bg-rose-600 text-white px-5 py-3 rounded-2xl shadow-lg font-black text-xs uppercase tracking-widest flex items-center gap-2 transition-all active:scale-95 animate-bounce-in"
            >
              <Trash2 size={18} /> Delete Selected ({selectedIds.size})
            </button>
          )}
          <button 
            onClick={() => {
              const csv = [
                ['Receipt ID', 'Date', 'Staff', 'Items', 'Amount', 'Payment'].join(','),
                ...filteredSales.map(s => [
                  s.id,
                  new Date(s.createdAt).toLocaleString(),
                  s.user?.name,
                  s.items.reduce((acc, i) => acc + i.quantity, 0),
                  s.total,
                  s.payments?.[0]?.method || 'N/A'
                ].join(','))
              ].join('\n');
              const blob = new Blob([csv], { type: 'text/csv' });
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `Sales_Ledger_${new Date().toISOString().split('T')[0]}.csv`;
              a.click();
            }}
            className="bg-theme-card border border-theme hover:border-brand text-theme-main px-5 py-3 rounded-2xl shadow-sm font-black text-xs uppercase tracking-widest flex items-center gap-2 transition-all"
          >
            <Download size={18} /> Export Audit
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {[
          { label: "Lifetime Revenue", value: `${currencySymbol}${stats.total.toLocaleString()}`, icon: ArrowUpRight, color: "bg-brand/10 text-brand" },
          { label: "Total Transactions", value: stats.count, icon: FileText, color: "bg-indigo-500/10 text-indigo-500" },
          { label: "Today's Revenue", value: `${currencySymbol}${stats.todayTotal.toLocaleString()}`, icon: Clock, color: "bg-emerald-500/10 text-emerald-500" },
          { label: "Today's Orders", value: stats.todayCount, icon: ShoppingBag, color: "bg-amber-500/10 text-amber-500" }
        ].map((item, i) => (
          <div key={i} className="bg-theme-card p-6 rounded-3xl border border-theme shadow-soft">
            <div className="flex justify-between items-start mb-4">
              <div className={`p-3 rounded-2xl ${item.color}`}>
                <item.icon size={20} />
              </div>
            </div>
            <p className="text-[10px] font-black text-theme-muted uppercase tracking-widest mb-1">{item.label}</p>
            <h4 className="text-xl font-black text-theme-main tracking-tight">{item.value}</h4>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-theme-card p-6 rounded-[2rem] border border-theme shadow-soft grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="space-y-2">
          <label className="text-[10px] font-black text-theme-muted uppercase tracking-widest ml-1">Search Receipt/Customer</label>
          <div className="relative">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-theme-muted" />
            <input 
              type="text" 
              placeholder="ID or Name..." 
              value={searchTerm}
              onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              className="w-full bg-theme-main border border-theme rounded-xl py-3 pl-10 pr-4 text-theme-main font-bold outline-none focus:ring-2 focus:ring-brand"
            />
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-[10px] font-black text-theme-muted uppercase tracking-widest ml-1">Filter by Staff</label>
          <div className="relative">
            <User size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-theme-muted" />
            <select 
              value={selectedUser}
              onChange={e => { setSelectedUser(e.target.value); setCurrentPage(1); }}
              className="w-full bg-theme-main border border-theme rounded-xl py-3 pl-10 pr-4 text-theme-main font-bold outline-none focus:ring-2 focus:ring-brand appearance-none"
            >
              <option value="">All Staff</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-[10px] font-black text-theme-muted uppercase tracking-widest ml-1">Start Date</label>
          <div className="relative">
            <Calendar size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-theme-muted" />
            <input 
              type="date" 
              value={dateRange.start}
              onChange={e => { setDateRange({...dateRange, start: e.target.value}); setCurrentPage(1); }}
              className="w-full bg-theme-main border border-theme rounded-xl py-3 pl-10 pr-4 text-theme-main font-bold outline-none focus:ring-2 focus:ring-brand"
            />
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-[10px] font-black text-theme-muted uppercase tracking-widest ml-1">End Date</label>
          <div className="relative">
            <Calendar size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-theme-muted" />
            <input 
              type="date" 
              value={dateRange.end}
              onChange={e => { setDateRange({...dateRange, end: e.target.value}); setCurrentPage(1); }}
              className="w-full bg-theme-main border border-theme rounded-xl py-3 pl-10 pr-4 text-theme-main font-bold outline-none focus:ring-2 focus:ring-brand"
            />
          </div>
        </div>
      </div>

      {/* Main Table */}
      <div className="bg-theme-card rounded-[2.5rem] border border-theme shadow-soft overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-theme-main/10 border-b border-theme text-[10px] font-black uppercase tracking-widest text-theme-muted">
                {isAdmin && (
                  <th className="pl-8 py-5 w-10">
                    <button onClick={toggleSelectAll} className="text-brand">
                      {selectedIds.size === currentSales.length && currentSales.length > 0 ? <CheckSquare size={20} /> : <Square size={20} />}
                    </button>
                  </th>
                )}
                <th className={isAdmin ? 'px-4 py-5' : 'px-8 py-5'}>Receipt ID</th>
                <th className="px-6 py-5">Date & Time</th>
                <th className="px-6 py-5">Salesperson</th>
                <th className="px-6 py-5 text-center">Items</th>
                <th className="px-6 py-5 text-right">Total Amount</th>
                <th className="px-6 py-5 text-center">Payment</th>
                <th className="px-8 py-5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-theme">
              {currentSales.map(sale => (
                <tr key={sale.id} className={`hover:bg-theme-main/5 transition-colors group ${selectedIds.has(sale.id) ? 'bg-brand/5' : ''}`}>
                  {isAdmin && (
                    <td className="pl-8 py-5">
                      <button onClick={() => toggleSelect(sale.id)} className={selectedIds.has(sale.id) ? 'text-brand' : 'text-theme-muted opacity-30 hover:opacity-100'}>
                        {selectedIds.has(sale.id) ? <CheckSquare size={20} /> : <Square size={20} />}
                      </button>
                    </td>
                  )}
                  <td className={isAdmin ? 'px-4 py-5' : 'px-8 py-5'}>
                    <span className="font-black text-brand text-xs">#{sale.id.slice(0, 8)}...</span>
                  </td>
                  <td className="px-6 py-5">
                    <div className="flex flex-col">
                      <span className="text-xs font-black text-theme-main">{new Date(sale.createdAt).toLocaleDateString()}</span>
                      <span className="text-[10px] font-bold text-theme-muted uppercase">{new Date(sale.createdAt).toLocaleTimeString()}</span>
                    </div>
                  </td>
                  <td className="px-6 py-5">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-theme-main text-theme-muted flex items-center justify-center font-black text-[10px] uppercase border border-theme">
                        {(sale.user?.name || 'S')[0]}
                      </div>
                      <span className="text-xs font-bold text-theme-main">{sale.user?.name || 'System'}</span>
                    </div>
                  </td>
                  <td className="px-6 py-5 text-center">
                    <span className="bg-theme-main px-3 py-1 rounded-lg text-[10px] font-black text-theme-main border border-theme">
                      {sale.items.reduce((acc, i) => acc + i.quantity, 0)} Items
                    </span>
                  </td>
                  <td className="px-6 py-5 text-right">
                    <div className="flex flex-col items-end">
                      {sale.refunds?.length > 0 ? (
                        <>
                          <span className="text-[10px] font-bold text-theme-muted line-through decoration-rose-500/50">
                            {currencySymbol}{sale.total.toFixed(2)}
                          </span>
                          <div className="flex items-center gap-1.5">
                            <span className="text-[10px] font-black text-rose-500 bg-rose-500/10 px-1.5 py-0.5 rounded uppercase tracking-tighter">
                               -{currencySymbol}{sale.refunds.reduce((acc, r) => acc + r.totalAmount, 0).toFixed(2)}
                            </span>
                            <span className="text-sm font-black text-theme-main">
                               {currencySymbol}{(sale.total - sale.refunds.reduce((acc, r) => acc + r.totalAmount, 0)).toFixed(2)}
                            </span>
                          </div>
                        </>
                      ) : (
                        <span className="font-black text-theme-main font-mono">
                          {currencySymbol}{sale.total.toFixed(2)}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-5 text-center">
                    <div className="flex items-center justify-center gap-2 text-[10px] font-black uppercase tracking-tight text-theme-muted">
                      <CreditCard size={14} className="opacity-40" />
                      {sale.payments?.[0]?.method || 'Cash'}
                    </div>
                  </td>
                  <td className="px-8 py-5 text-right">
                    <div className="flex justify-end gap-2">
                      <button 
                        onClick={() => navigate(`/app/receipt/${sale.id}`)}
                        className="p-2 bg-brand/10 text-brand hover:bg-brand hover:text-white rounded-xl transition-all shadow-sm active:scale-95"
                        title="View Details"
                      >
                        <Eye size={18} />
                      </button>
                      {isAdmin && (
                        <button 
                          onClick={() => handleDelete(sale.id)}
                          className="p-2 bg-rose-500/10 text-rose-500 hover:bg-rose-500 hover:text-white rounded-xl transition-all shadow-sm active:scale-95"
                          title="Delete Sale"
                        >
                          <Trash2 size={18} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {currentSales.length === 0 && !loading && (
                <tr>
                  <td colSpan={isAdmin ? "8" : "7"} className="px-8 py-20 text-center">
                    <div className="flex flex-col items-center">
                      <ShoppingBag size={48} className="text-theme-muted opacity-20 mb-4" />
                      <h3 className="text-xl font-black text-theme-main uppercase tracking-tight mb-1">No Transactions Found</h3>
                      <p className="text-theme-muted text-sm font-medium">Try adjusting your filters or search terms.</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="px-8 py-6 bg-theme-main/5 border-t border-theme flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-xs font-bold text-theme-muted uppercase tracking-widest">
              Showing <span className="text-theme-main">{indexOfFirstItem + 1}</span> to <span className="text-theme-main">{Math.min(indexOfLastItem, totalItems)}</span> of <span className="text-theme-main">{totalItems}</span> transactions
            </p>
            <div className="flex items-center gap-2">
              <button 
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-2 rounded-xl bg-theme-card border border-theme text-theme-muted disabled:opacity-20 hover:border-brand transition-all"
              >
                <ChevronLeft size={20} />
              </button>
              
              {(() => {
                const pages = [];
                let start = Math.max(1, currentPage - 1);
                let end = Math.min(totalPages, start + 2);
                if (end - start < 2) start = Math.max(1, end - 2);
                for (let i = start; i <= end; i++) pages.push(i);
                return pages.map(p => (
                  <button
                    key={p}
                    onClick={() => paginate(p)}
                    className={`w-10 h-10 rounded-xl font-black text-xs transition-all ${
                      currentPage === p 
                        ? 'bg-brand text-white shadow-lg shadow-brand/30' 
                        : 'bg-theme-card border border-theme text-theme-muted hover:border-brand'
                    }`}
                  >
                    {p}
                  </button>
                ));
              })()}

              <button 
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="p-2 rounded-xl bg-theme-card border border-theme text-theme-muted disabled:opacity-20 hover:border-brand transition-all"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
